import * as Sentry from '@sentry/browser'

type Extension = {
  eid: string,
  page: string,
  detail: string
}
type ResponseCallback = (response: any) => void

export const useChromeExtensionStore = defineStore('chromeExtension', () => {
  const snackbar = useSnackbar()
  const chromeExtensionPrefs = ref({
    extension: null as Extension | null
  })

  const eid = computed(() => chromeExtensionPrefs.value.extension?.eid || null)

  const canMessageExtension = () => typeof chrome !== 'undefined' && !!chrome.runtime?.sendMessage

  const extension = () => computed({
    get: () => chromeExtensionPrefs.value.extension,
    set: (value) => {
      chromeExtensionPrefs.value.extension = value
    }
  })

  const login = async () => {
    try {
      if (!eid.value || !canMessageExtension()) { return }
      const { getEffectiveAuthTokenRaw } = useEffectiveAuthToken()
      const token = await getEffectiveAuthTokenRaw()
      if (token) {
        chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-login', jwt: token })
      }
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Error during login to Chrome Extension')
    }
  }

  const logout = () => {
    if (!eid.value) { return }
    if (canMessageExtension()) {
      chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-logout' })
    }
    chromeExtensionPrefs.value.extension = null
  }

  const sendCurrentPage = (currentPage: string) => {
    if (!eid.value || !canMessageExtension()) { return }
    chrome.runtime.sendMessage(eid.value, { greeting: 'emissary-set-current-page', currentPage })
  }

  const askForCampaignData = (callback: ResponseCallback) => {
    if (!eid.value || !canMessageExtension()) { return }
    chrome.runtime.sendMessage(eid.value, { greeting: 'get-campaign-data' }, {}, callback)
  }

  const getScreenshotAndHtmlTable = (callback: ResponseCallback) => {
    try {
      if (!chromeExtensionPrefs.value.extension?.eid || !canMessageExtension()) { return }
      chrome.runtime.sendMessage(chromeExtensionPrefs.value.extension?.eid, { greeting: 'get-screenshot-and-table-html' }, {}, callback)
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Error getting information from Chrome Extension')
    }
  }

  return {
    editable: {
      extension
    },
    login,
    logout,
    sendCurrentPage,
    askForCampaignData,
    getScreenshotAndHtmlTable
  }
})
